import { useEffect, useState } from 'react';
import { fetchSettlements } from '@/lib/api/agritour';
import AgriTourSelect from './AgriTourSelect';
import {
  AGRITOUR_SECTIONS,
  LEGAL_ENTITY_OPTIONS,
  YES_NO,
} from './agritour-form-copy';
import type { AgriTourFormData, AgriTourFiles } from './AgriTourScreen';
import styles from './AgriTourScreen.module.css';

interface AgriTourStepAProps {
  formData: AgriTourFormData;
  files: AgriTourFiles;
  fieldErrors: Record<string, string>;
  onChange: (patch: Partial<AgriTourFormData>) => void;
  onFileChange: (key: keyof AgriTourFiles, file: File | null) => void;
}

export default function AgriTourStepA({
  formData,
  files,
  fieldErrors,
  onChange,
  onFileChange,
}: AgriTourStepAProps) {
  const [settlements, setSettlements] = useState<{ id: string; name: string }[]>([]);
  const [settlementsLoading, setSettlementsLoading] = useState(false);
  const [settlementsError, setSettlementsError] = useState<string | null>(null);

  useEffect(() => {
    setSettlementsLoading(true);
    fetchSettlements()
      .then((data) => {
        setSettlements(data);
        setSettlementsError(null);
      })
      .catch((err: unknown) => {
        const message =
          err instanceof Error ? err.message : 'שגיאה בטעינת רשימת היישובים';
        setSettlements([]);
        setSettlementsError(message);
      })
      .finally(() => {
        setSettlementsLoading(false);
      });
  }, []);

  return (
    <section aria-label={AGRITOUR_SECTIONS.a.title}>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          שם מלא של היזם / מגיש הבקשה
        </label>
        <input
          type="text"
          className={`${styles.input} ${fieldErrors.applicantName ? styles.inputError : ''}`}
          value={formData.applicantName}
          onChange={(e) => onChange({ applicantName: e.target.value })}
        />
        {fieldErrors.applicantName && (
          <p className={styles.fieldError} role="alert">{fieldErrors.applicantName}</p>
        )}
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          מספר זיהוי (ח.פ. / מספר שותפות / ת.ז.)
        </label>
        <input
          type="text"
          inputMode="numeric"
          className={`${styles.input} ${fieldErrors.idNumber ? styles.inputError : ''}`}
          value={formData.idNumber}
          onChange={(e) => onChange({ idNumber: e.target.value })}
        />
        {fieldErrors.idNumber && (
          <p className={styles.fieldError} role="alert">{fieldErrors.idNumber}</p>
        )}
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          ישות משפטית של המבקש
        </label>
        <p className={styles.fieldHint}>אנא בחר את הסטטוס המשפטי של מגיש הבקשה מהרשימה למטה.</p>
        <AgriTourSelect
          value={formData.legalEntity}
          options={LEGAL_ENTITY_OPTIONS}
          onChange={(v) => onChange({ legalEntity: v })}
          hasError={Boolean(fieldErrors.legalEntity)}
        />
        {fieldErrors.legalEntity && (
          <p className={styles.fieldError} role="alert">{fieldErrors.legalEntity}</p>
        )}
      </div>

      {formData.legalEntity === '5' && (
        <div className={`${styles.field} ${styles.conditional}`}>
          <label className={`${styles.fieldLabel} ${styles.required}`}>
            ישות משפטית של המבקש - אחר
          </label>
          <p className={styles.fieldHint}>יש לפרט</p>
          <input
            type="text"
            className={`${styles.input} ${fieldErrors.legalEntityOther ? styles.inputError : ''}`}
            value={formData.legalEntityOther}
            onChange={(e) => onChange({ legalEntityOther: e.target.value })}
          />
          {fieldErrors.legalEntityOther && (
            <p className={styles.fieldError} role="alert">{fieldErrors.legalEntityOther}</p>
          )}
        </div>
      )}

      <div className={styles.field}>
        <label className={styles.fieldLabel}>שם המיזם המתוכנן/הקיים</label>
        <input
          type="text"
          className={styles.input}
          value={formData.ventureName}
          onChange={(e) => onChange({ ventureName: e.target.value })}
        />
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          שם היישוב שבו יוקם המיזם
        </label>
        <p className={styles.fieldHint}>
          מבין ישובי המועצות האזוריות: מרום הגליל, הגליל העליון ומבואות החרמון
        </p>
        {settlementsError && <p className={styles.fieldError} role="alert">{settlementsError}</p>}
        <AgriTourSelect
          value={formData.settlementItemId}
          options={settlements.map((s) => ({ value: s.id, label: s.name }))}
          onChange={(v) => onChange({ settlementItemId: v })}
          hasError={Boolean(fieldErrors.settlementItemId)}
          disabled={settlementsLoading || Boolean(settlementsError)}
          searchable
          placeholder={
            settlementsLoading
              ? 'טוען יישובים...'
              : settlementsError
                ? 'לא זמין'
                : '— בחר יישוב —'
          }
          emptyMessage={
            settlementsError
              ? settlementsError
              : 'רשימת היישובים לא זמינה — נסו לרענן את הדף'
          }
        />
        {fieldErrors.settlementItemId && (
          <p className={styles.fieldError} role="alert">{fieldErrors.settlementItemId}</p>
        )}
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>שם איש הקשר לפרויקט</label>
        <input
          type="text"
          className={styles.input}
          value={formData.contactPerson}
          onChange={(e) => onChange({ contactPerson: e.target.value })}
        />
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>טלפון נייד</label>
        <p className={styles.fieldHint}>יש להזין מספר טלפון נייד לצורך יצירת קשר.</p>
        <div className={styles.phoneRow}>
          <span className={styles.phonePrefix} dir="ltr">+972</span>
          <input
            type="tel"
            className={`${styles.input} ${fieldErrors.phone ? styles.inputError : ''}`}
            value={formData.phone}
            onChange={(e) => onChange({ phone: e.target.value })}
            placeholder="05X-XXXXXXX"
            dir="ltr"
          />
        </div>
        {fieldErrors.phone && <p className={styles.fieldError} role="alert">{fieldErrors.phone}</p>}
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          כתובת דואר אלקטרוני
        </label>
        <p className={styles.fieldHint}>יש להזין כתובת דואר אלקטרוני עדכנית.</p>
        <input
          type="email"
          className={`${styles.input} ${fieldErrors.email ? styles.inputError : ''}`}
          value={formData.email}
          onChange={(e) => onChange({ email: e.target.value })}
          placeholder="example@mail.com"
          dir="ltr"
        />
        {fieldErrors.email && <p className={styles.fieldError} role="alert">{fieldErrors.email}</p>}
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          האם בעל השליטה במיזם הינו משרת מילואים פעיל?
        </label>
        <p className={styles.fieldHint}>
          בצע לפחות 20 ימי מילואים ב-12 החודשים האחרונים – מעניק 10% תוספת לניקוד הבקשה.
        </p>
        <AgriTourSelect
          value={formData.miluimActive}
          options={YES_NO}
          onChange={(v) => onChange({ miluimActive: v })}
          hasError={Boolean(fieldErrors.miluimActive)}
        />
        {fieldErrors.miluimActive && (
          <p className={styles.fieldError} role="alert">{fieldErrors.miluimActive}</p>
        )}
      </div>

      {formData.miluimActive === '0' && (
        <div className={`${styles.field} ${styles.conditional}`}>
          <label className={`${styles.fieldLabel} ${styles.required}`}>אישור משרת מילואים פעיל</label>
          <p className={styles.fieldHint}>יש לצרף אישור משרת מילואים פעיל</p>
          {fieldErrors.filezgshkkm2 && (
            <p className={styles.fieldError} role="alert">{fieldErrors.filezgshkkm2}</p>
          )}
          <div className={styles.fileRow}>
            <label className={styles.fileButton}>
              בחר קובץ
              <input
                type="file"
                style={{ display: 'none' }}
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => onFileChange('filezgshkkm2', e.target.files?.[0] ?? null)}
              />
            </label>
            {files.filezgshkkm2 && (
              <span className={styles.fileName}>{files.filezgshkkm2.name}</span>
            )}
          </div>
        </div>
      )}

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          תיאור המיזם, החזון ואופי הפעילות התיירותית-חקלאית
        </label>
        <p className={styles.fieldHint}>
          אנא פרטו בקצרה את המיזם שלכם, החזון שמנחה אותו ואת אופי הפעילות התיירותית-חקלאית,
          לדוגמה מרכז מבקרים, סיורים ועוד.
        </p>
        <textarea
          className={`${styles.textarea} ${fieldErrors.ventureDescription ? styles.textareaError : ''}`}
          value={formData.ventureDescription}
          onChange={(e) => onChange({ ventureDescription: e.target.value })}
          rows={5}
        />
        {fieldErrors.ventureDescription && (
          <p className={styles.fieldError} role="alert">{fieldErrors.ventureDescription}</p>
        )}
      </div>
    </section>
  );
}
